import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Link, Redirect, useHistory } from "react-router-dom";
import {
  FaCalendarAlt,
  FaChalkboardTeacher,
  FaExchangeAlt,
  FaHome,
  FaSyncAlt,
  FaUsers,
} from "react-icons/fa";
import MyLoader from "../Spinner/MyLoader";
import SessionManager from "../Utils/SessionManager";
import myToast from "../Utils/MyToast";
import COLORS from "../resources/colors/COLORS";
import BackButton from "../ChildComponent/BackButton";
import MdlSelectSession from "../dialog/MdlSelectSession";
import "../css/Admin_css/admin-customize.css";
import "../css/Admin_css/payroll.css";

const API_BASE = () =>
  `${process.env.REACT_APP_SERVER_NAME}${process.env.REACT_APP_TARGET_DIR}`;

const HERO_GRADIENT = `linear-gradient(135deg, ${COLORS.primary_color_shade5} 0%, ${COLORS.primary_color_shade7} 55%, ${COLORS.primary_color_shade9} 100%)`;

const PAGE_FONT =
  'Inter, system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif';

//Created By Cursor
function sectionsForClass(allSections, classId) {
  if (!Array.isArray(allSections)) return [];
  return allSections.filter(
    (row) => String(row.class_id) === String(classId)
  );
}

//Created By Cursor
function studentCountFor(studentCounts, classId, sectionId) {
  if (!Array.isArray(studentCounts)) return 0;
  const match = studentCounts.find(
    (row) =>
      String(row.class_id) === String(classId) &&
      String(row.section_id) === String(sectionId)
  );
  return Number(match?.total_student || 0);
}

//Created By Cursor
export default function ClassList() {
  const history = useHistory();
  const loggedIn = localStorage.getItem("token") != null;

  const [loading, setLoading] = useState(false);
  const [isOpenSessionDialog, setIsOpenSessionDialog] = useState(false);

  const [sessionId, setSessionId] = useState("");
  const [sessionName, setSessionName] = useState("");
  const [sessionStatus, setSessionStatus] = useState("");

  const [classData, setClassData] = useState([]);
  const [classBoard, setClassBoard] = useState([]);
  const [classMedium, setClassMedium] = useState([]);
  const [allSections, setAllSections] = useState([]);
  const [studentCounts, setStudentCounts] = useState([]);
  const [classTeachers, setClassTeachers] = useState([]);
  const [boardId, setBoardId] = useState("");
  const [mediumId, setMediumId] = useState("");

  //Created By Cursor
  const teacherFor = useCallback((teachers, sections, classId, sectionId) => {
    const fromSection = Array.isArray(sections)
      ? sections.find(
          (row) =>
            String(row.class_id) === String(classId) &&
            String(row.section_id) === String(sectionId)
        )
      : null;
    if (fromSection?.class_teacher_name) {
      return {
        class_teacher_id: fromSection.class_teacher_id || "",
        class_teacher_name: fromSection.class_teacher_name,
      };
    }
    const fromMap = Array.isArray(teachers)
      ? teachers.find(
          (row) =>
            String(row.class_id) === String(classId) &&
            String(row.section_id) === String(sectionId)
        )
      : null;
    return {
      class_teacher_id: fromMap?.class_teacher_id || "",
      class_teacher_name: fromMap?.class_teacher_name || "",
    };
  }, []);

  const loadClasses = useCallback(async (sid) => {
    if (!sid) return;
    setLoading(true);
    try {
      const schoolCode = SessionManager.getSchoolCode();
      const params = { school_code: schoolCode, session_id: sid };
      const url = `${API_BASE()}getClassWithStudentCount.php`;

      const response = await axios.get(url, { params }).then((res) => res.data);
      if (response?.api_status === "OK") {
        const boards = Array.isArray(response.data?.board)
          ? response.data.board
          : [];
        const mediums = Array.isArray(response.data?.medium)
          ? response.data.medium
          : [];
        setClassBoard(boards);
        setClassMedium(mediums);
        setAllSections(
          Array.isArray(response.data?.all_section)
            ? response.data.all_section
            : []
        );
        setStudentCounts(
          Array.isArray(response.data?.student_count)
            ? response.data.student_count
            : []
        );
        setClassTeachers(
          Array.isArray(response.data?.class_teachers)
            ? response.data.class_teachers
            : []
        );
        setClassData(
          Array.isArray(response.data?.class) ? response.data.class : []
        );
        setBoardId((prev) => prev || boards[0]?.board_id || "");
        setMediumId((prev) => prev || mediums[0]?.medium_id || "");
      } else {
        setClassBoard([]);
        setClassMedium([]);
        setAllSections([]);
        setStudentCounts([]);
        setClassTeachers([]);
        setClassData([]);
        myToast.notify_warning(
          response?.api_remark || "Failed to load classes."
        );
      }
    } catch (error) {
      console.error(error);
      myToast.notify_warning("Failed to load classes.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
    const mSession = SessionManager.getCurrentSession()?.[0];
    if (!mSession?.session_id) return;
    setSessionId(mSession.session_id);
    setSessionName(mSession.session_name || "");
    setSessionStatus(mSession.status || "");
    loadClasses(mSession.session_id);
  }, [loadClasses]);

  const filteredClassSections = useMemo(() => {
    if (!boardId || !mediumId || !classData.length) return [];

    const filtered = classData.filter(
      (item) =>
        String(item.board_id) === String(boardId) &&
        String(item.medium_id) === String(mediumId)
    );

    const rows = [];
    filtered.forEach((item) => {
      const sections = sectionsForClass(allSections, item.class_id);
      if (!sections.length) {
        const teacher = teacherFor(
          classTeachers,
          allSections,
          item.class_id,
          "No"
        );
        rows.push({
          ...item,
          section_id: "No",
          section_name: "",
          class_teacher_id: teacher.class_teacher_id,
          class_teacher_name: teacher.class_teacher_name,
          student: studentCountFor(studentCounts, item.class_id, "No"),
        });
        return;
      }
      sections.forEach((section) => {
        const teacher = teacherFor(
          classTeachers,
          allSections,
          item.class_id,
          section.section_id
        );
        rows.push({
          ...section,
          class_name: item.class_name,
          board_id: item.board_id,
          board_name: item.board_name,
          medium_id: item.medium_id,
          medium_name: item.medium_name,
          session_id: item.session_id || sessionId,
          class_teacher_id:
            section.class_teacher_id || teacher.class_teacher_id,
          class_teacher_name:
            section.class_teacher_name || teacher.class_teacher_name,
          student: studentCountFor(
            studentCounts,
            item.class_id,
            section.section_id
          ),
        });
      });
    });
    return rows;
  }, [
    boardId,
    mediumId,
    classData,
    allSections,
    studentCounts,
    classTeachers,
    sessionId,
    teacherFor,
  ]);

  const totalStudents = useMemo(
    () =>
      filteredClassSections.reduce(
        (sum, row) => sum + (Number(row.student) || 0),
        0
      ),
    [filteredClassSections]
  );

  const handleSelectedSession = (sid, sname, sstatus) => {
    setSessionId(sid);
    setSessionName(sname);
    setSessionStatus(sstatus);
    setIsOpenSessionDialog(false);
    setBoardId("");
    setMediumId("");
    loadClasses(sid);
  };

  const sessionMeta = useMemo(() => {
    const current = SessionManager.getCurrentSession()?.[0];
    return {
      session_id: sessionId,
      session_name: sessionName,
      status: sessionStatus,
      start_date: current?.start_date,
      end_date: current?.end_date,
    };
  }, [sessionId, sessionName, sessionStatus]);

  if (!loggedIn) {
    return <Redirect to="/Login" />;
  }

  return (
    <div
      className="content payroll-page sm-page cl-page"
      style={{ fontFamily: PAGE_FONT }}
    >
      <div
        className="content-header d-flex justify-content-between align-items-center"
        style={{ background: COLORS.primary_color_shade5 }}
      >
        <div className="leftside-content-header d-flex align-items-center gap-2">
          <button
            type="button"
            className="qb-home-btn"
            aria-label="Home"
            onClick={() => history.push("/AdminMain")}
          >
            <FaHome />
          </button>
          <ul className="breadcrumbs mb-0">
            <li>
              <span style={{ color: COLORS.white }}>Class List</span>
            </li>
          </ul>
        </div>
        <div className="rightside-content-header">
          <BackButton />
        </div>
      </div>

      <div className="bg_page_white_rounded box_shadow animated fadeInUp sm-shell cl-shell">
        <header className="sm-hero" style={{ "--sm-hero-bg": HERO_GRADIENT }}>
          <div className="sm-hero__brand">
            <div className="sm-hero__icon" aria-hidden>
              <FaChalkboardTeacher />
            </div>
            <div className="min-w-0">
              <h1 className="sm-hero__title">Class List</h1>
              <p className="sm-hero__sub">
                {sessionName || "Session"}
                {sessionStatus ? ` · ${sessionStatus}` : ""}
                {` · ${filteredClassSections.length} class${
                  filteredClassSections.length === 1 ? "" : "es"
                }`}
                {` · ${totalStudents} student${
                  totalStudents === 1 ? "" : "s"
                }`}
              </p>
            </div>
          </div>
          <div className="sm-hero__tools">
            <button
              type="button"
              className="cl-session-btn"
              onClick={() => setIsOpenSessionDialog(true)}
              disabled={loading}
              title="Change session"
            >
              <span className="cl-session-btn__icon" aria-hidden>
                <FaCalendarAlt />
              </span>
              <span className="cl-session-btn__text">
                <small>Session</small>
                <strong>{sessionName || "Select session"}</strong>
              </span>
              <span className="cl-session-btn__change" aria-hidden>
                <FaExchangeAlt />
                <em>Change</em>
              </span>
            </button>
            <select
              className="pp-pill-select"
              value={boardId}
              onChange={(e) => setBoardId(e.target.value)}
              disabled={loading || classBoard.length === 0}
              aria-label="Board"
            >
              {classBoard.length === 0 ? (
                <option value="">No boards</option>
              ) : (
                classBoard.map((item) => (
                  <option key={item.board_id} value={item.board_id}>
                    {item.board_name}
                  </option>
                ))
              )}
            </select>
            <select
              className="pp-pill-select"
              value={mediumId}
              onChange={(e) => setMediumId(e.target.value)}
              disabled={loading || classMedium.length === 0}
              aria-label="Medium"
            >
              {classMedium.length === 0 ? (
                <option value="">No medium</option>
              ) : (
                classMedium.map((item) => (
                  <option key={item.medium_id} value={item.medium_id}>
                    {item.medium_name}
                  </option>
                ))
              )}
            </select>
            <button
              type="button"
              className={`pp-icon-btn${loading ? " spinning" : ""}`}
              onClick={() => loadClasses(sessionId)}
              disabled={loading || !sessionId}
              aria-label="Refresh"
              title="Refresh"
            >
              <FaSyncAlt aria-hidden />
            </button>
          </div>
        </header>

        <div className="cl-body">
          {loading && !filteredClassSections.length ? (
            <div className="cl-loading">
              <MyLoader />
            </div>
          ) : null}

          {!loading && !filteredClassSections.length ? (
            <div className="cl-empty">
              {classData.length
                ? "No classes for this board and medium."
                : "No classes found for this session."}
            </div>
          ) : (
            <div className="cl-grid">
              {filteredClassSections.map((item) => {
                const sectionId =
                  item.section_id === "No" ? "No" : item.section_id;
                const title =
                  item.section_id === "No"
                    ? `Class: ${item.class_name}`
                    : `${item.class_name} · ${item.section_name}`;
                return (
                  <Link
                    key={`${item.class_id}-${sectionId}`}
                    className="cl-card-link"
                    to={{
                      pathname: `/AdminMain/StudentList/${item.class_id}/${sectionId}/${
                        item.session_id || sessionId
                      }`,
                      state: { sessionMeta },
                    }}
                  >
                    <article className="cl-card">
                      <div className="cl-card__count">
                        <strong>{item.student}</strong>
                        <span>
                          <FaUsers aria-hidden /> Students
                        </span>
                      </div>
                      <div className="cl-card__body">
                        <h2>{title}</h2>
                        <div className="cl-card__meta cl-card__teacher">
                          <span>Class Teacher</span>
                          <strong>
                            {item.class_teacher_name || "Not assigned"}
                          </strong>
                        </div>
                        <div className="cl-card__board-medium">
                          ({item.board_name || "Board"}-
                          {item.medium_name || "Medium"})
                        </div>
                      </div>
                    </article>
                  </Link>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {isOpenSessionDialog ? (
        <MdlSelectSession
          action={() => setIsOpenSessionDialog(false)}
          action2={handleSelectedSession}
        />
      ) : null}
    </div>
  );
}
